import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  Button,
  Alert,
  CircularProgress,
  TextField,
  Grid,
  Chip,
  IconButton,
  Tooltip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  InputAdornment,
  Stack,
  Card,
  CardContent,
  Breadcrumbs,
  Link,
  Container,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions
} from '@mui/material';
import {
  Add as AddIcon,
  Search as SearchIcon,
  Visibility as ViewIcon,
  Description as DocumentIcon,
  Clear as ClearIcon,
  Home as HomeIcon,
  Delete as DeleteIcon
} from '@mui/icons-material';
import { useAuthenticatedFetch } from '../../../hooks/useAuthenticatedFetch';

interface IdentifiedDocument {
  id: number;
  construction_id: string;
  document_type: string;
  document_name: string;
  document_identifier: string;
  worksheet_name: string;
  start_cell: string;
  end_cell: string;
  comparer: string;
  search_string: string;
  azure_blob_path: string;
  created_at: string;
  created_by: string;
}

interface PaginationInfo {
  page: number;
  limit: number;
  totalCount: number;
  totalPages: number;
  hasNextPage: boolean;
  hasPreviousPage: boolean;
}

interface ApiResponse {
  success: boolean;
  data: IdentifiedDocument[];
  pagination: PaginationInfo;
  search: string | null;
  error?: string;
  message?: string;
}

export const DocumentListPage: React.FC = () => {
  const [documents, setDocuments] = useState<IdentifiedDocument[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [pagination, setPagination] = useState<PaginationInfo | null>(null);

  // Delete confirmation dialog state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [documentToDelete, setDocumentToDelete] = useState<IdentifiedDocument | null>(null);
  const [deleting, setDeleting] = useState(false);

  const { authenticatedFetch } = useAuthenticatedFetch();
  const navigate = useNavigate();

  const fetchDocuments = async (searchQuery: string = '', pageNum: number = 0, limit: number = 25) => {
    setLoading(true);
    setError(null);

    try {
      const queryParams = new URLSearchParams({
        page: (pageNum + 1).toString(), // API uses 1-based pages, MUI uses 0-based
        limit: limit.toString()
      });

      if (searchQuery.trim()) {
        queryParams.append('search', searchQuery.trim());
      }

      const response = await authenticatedFetch(`/api/appraisals/admin/documents?${queryParams}`);

      if (!response) {
        throw new Error('Failed to fetch documents - authentication error');
      }

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to fetch documents');
      }

      const data: ApiResponse = await response.json();

      if (data.success) {
        setDocuments(data.data);
        setPagination(data.pagination);
        setSearchTerm(searchQuery);
      } else {
        throw new Error(data.error || 'Failed to fetch documents');
      }

    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred while fetching documents');
      setDocuments([]);
      setPagination(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDocuments('', page, rowsPerPage);
  }, [page, rowsPerPage]);

  const handleSearch = () => {
    setPage(0); // Reset to first page when searching
    fetchDocuments(searchInput, 0, rowsPerPage);
  };

  const handleClearSearch = () => {
    setSearchInput('');
    setPage(0);
    fetchDocuments('', 0, rowsPerPage);
  };

  const handleKeyPress = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter') {
      handleSearch();
    }
  };

  const handleChangePage = (_: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newRowsPerPage = parseInt(event.target.value, 10);
    setRowsPerPage(newRowsPerPage);
    setPage(0);
  };

  const handleViewDocument = (documentId: number) => {
    navigate(`/admin/deliverables/doc-id-system/documents/${documentId}`);
  };

  const handleCreateDocument = () => {
    navigate('/admin/deliverables/doc-id-system/new');
  };

  const handleDeleteDocument = (document: IdentifiedDocument) => {
    setDocumentToDelete(document);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!documentToDelete) return;

    setDeleting(true);
    setError(null);

    try {
      const response = await authenticatedFetch(`/api/appraisals/admin/documents/${documentToDelete.id}/delete`, {
        method: 'DELETE'
      });

      if (!response) {
        throw new Error('Failed to delete document - authentication error');
      }

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to delete document');
      }

      // Refresh the documents list
      fetchDocuments(searchTerm, page, rowsPerPage);

      // Close dialog and reset state
      setDeleteDialogOpen(false);
      setDocumentToDelete(null);

    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred while deleting the document');
    } finally {
      setDeleting(false);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setDocumentToDelete(null);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString();
  };

  const formatCellRange = (startCell: string, endCell: string) => {
    return startCell === endCell ? startCell : `${startCell}:${endCell}`;
  };

  return (
    <Container maxWidth="xl">
      <Box sx={{ py: 4 }}>
        {/* Breadcrumb */}
        <Breadcrumbs sx={{ mb: 3 }}>
          <Link
            color="inherit"
            href="/"
            onClick={(e) => {
              e.preventDefault();
              navigate('/');
            }}
            sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}
          >
            <HomeIcon sx={{ mr: 0.5 }} fontSize="small" />
            Home
          </Link>
          <Link
            color="inherit"
            href="/admin/deliverables"
            onClick={(e) => {
              e.preventDefault();
              navigate('/admin/deliverables');
            }}
            sx={{ cursor: 'pointer' }}
          >
            Deliverables Admin
          </Link>
          <Typography color="text.primary">Document Identification System</Typography>
        </Breadcrumbs>

        {/* Header */}
        <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <Box>
            <Typography variant="h4" component="h1" gutterBottom>
              Document Identification System
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Manage all Excel documents that have been processed through the Document Identifier System.
            </Typography>
          </Box>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleCreateDocument}
            sx={{ minWidth: '200px' }}
          >
            Create New Document
          </Button>
        </Box>

        {/* Search Bar */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={8}>
                <TextField
                  fullWidth
                  placeholder="Search documents by name, identifier, worksheet, or construction ID..."
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  onKeyPress={handleKeyPress}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon color="action" />
                      </InputAdornment>
                    ),
                    endAdornment: searchInput && (
                      <InputAdornment position="end">
                        <IconButton onClick={handleClearSearch} size="small">
                          <ClearIcon />
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <Stack direction="row" spacing={1}>
                  <Button
                    variant="contained"
                    onClick={handleSearch}
                    disabled={loading}
                    startIcon={<SearchIcon />}
                  >
                    Search
                  </Button>
                  {searchTerm && (
                    <Button
                      variant="outlined"
                      onClick={handleClearSearch}
                      disabled={loading}
                    >
                      Clear
                    </Button>
                  )}
                </Stack>
              </Grid>
            </Grid>
            {searchTerm && (
              <Box sx={{ mt: 2 }}>
                <Chip
                  label={`Searching for: "${searchTerm}"`}
                  onDelete={handleClearSearch}
                  color="primary"
                  variant="outlined"
                />
              </Box>
            )}
          </CardContent>
        </Card>

      {/* Content */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      ) : documents.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <DocumentIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h6" gutterBottom>
            {searchTerm ? 'No documents found' : 'No documents yet'}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            {searchTerm
              ? `No documents match your search for "${searchTerm}"`
              : 'Upload your first Excel document to get started'
            }
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleCreateDocument}
          >
            Create First Document
          </Button>
        </Paper>
      ) : (
        <Paper>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Document Name</TableCell>
                  <TableCell>Document Type</TableCell>
                  <TableCell>Identifier</TableCell>
                  <TableCell>Created</TableCell>
                  <TableCell align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {documents.map((document) => (
                  <TableRow key={document.id} hover>
                    <TableCell>
                      <Typography variant="body2" fontWeight="medium">
                        {document.document_name}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {document.document_type.toUpperCase()}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontFamily="monospace">
                        {document.document_identifier}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {formatDate(document.created_at)}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Stack direction="row" spacing={1} justifyContent="center">
                        <Tooltip title="View Document Details">
                          <IconButton
                            onClick={() => handleViewDocument(document.id)}
                            size="small"
                            color="primary"
                          >
                            <ViewIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete Document">
                          <IconButton
                            onClick={() => handleDeleteDocument(document)}
                            size="small"
                            color="error"
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          {pagination && (
            <TablePagination
              rowsPerPageOptions={[10, 25, 50, 100]}
              component="div"
              count={pagination.totalCount}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              labelDisplayedRows={({ from, to, count }) =>
                `${from}-${to} of ${count !== -1 ? count : `more than ${to}`}`
              }
            />
          )}
        </Paper>
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={handleDeleteCancel}
        aria-labelledby="delete-document-dialog-title"
        aria-describedby="delete-document-dialog-description"
      >
        <DialogTitle id="delete-document-dialog-title">
          Delete Document
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="delete-document-dialog-description">
            Are you sure you want to delete the document "{documentToDelete?.document_name}"?
            <br />
            <br />
            <strong>This action cannot be undone.</strong> All associated data sets and worksheets will also be permanently deleted.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleDeleteCancel}
            disabled={deleting}
          >
            Cancel
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            color="error"
            variant="contained"
            disabled={deleting}
            startIcon={deleting ? <CircularProgress size={16} /> : <DeleteIcon />}
          >
            {deleting ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
      </Box>
    </Container>
  );
};